import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { can } from '../../utils/permissions';
import useLiveChannel from '../../hooks/useLiveChannel';
import useChatPush from '../../hooks/useChatPush';
import { SAFETY_NET_MS } from '../../utils/live/wsTransport';
import { READ_EVENT } from './Conversation';

/*
 * The header's way into Chat, with how many messages wait (GET /chat/unread's
 * `total`: muted and silenced conversations left out). Asked every 30 seconds
 * while the page is shown, never while it is hidden, and at once when a
 * conversation is read. Nothing at all for an account that has no chat (the
 * public survey's). `as` draws it as the header's own icon button.
 *
 * `onChat` opens the chat where the host keeps it: Layout's drawer
 * (components/chat/ChatDrawer.js, ?chat=inbox; plan phase 8). Without it the
 * button goes to the /chat page, as on that page itself.
 */
const ChatButton = forwardRef(function ChatButton({ user, label, as: Button, className, onOpen, onChat }, ref) {
  const navigate = useNavigate();
  const allowed = can(user, 'chat:read');
  const total = useChatUnread(user);

  if (!allowed) return null;
  const shown = total > 99 ? '99+' : String(total);
  return (
    <Button ref={ref} className={className} label={total ? `${label} (${shown})` : label} data-testid="nav-chat"
      aria-haspopup={onChat ? 'dialog' : undefined}
      onClick={() => { onOpen?.(); if (onChat) onChat(); else navigate('/chat'); }}>
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <ChatIcon />
        {total > 0 && (
          <span data-testid="nav-chat-unread" aria-hidden="true"
            style={{ position: 'absolute', top: -6, insetInlineEnd: -8, background: 'var(--c-danger, #c0392b)',
              color: '#fff', borderRadius: 999, fontSize: 10, lineHeight: '15px', minWidth: 15, padding: '0 4px',
              textAlign: 'center', fontWeight: 700 }}>
            {shown}
          </span>
        )}
      </span>
    </Button>
  );
});
export default ChatButton;

/** How many messages wait for `user` (0 without chat), asked as ChatButton
 *  asks: also by the Field camera's messages tab. `enabled` false asks nothing.
 *  While the chat's socket is open, any poke asks at once (phase D2). */
export function useChatUnread(user, { enabled = true } = {}) {
  const allowed = enabled && can(user, 'chat:read');
  const [total, setTotal] = useState(0);
  const pull = useCallback(() => api.get('/chat/unread').then((r) => r?.data), []);
  const pokeRef = useRef(null);
  const live = useChatPush({ enabled: allowed, onPoke: () => pokeRef.current?.() });
  const poke = useLiveChannel(pull, (data) => setTotal(Number.isInteger(data?.total) ? data.total : 0),
    { enabled: allowed, base: live ? SAFETY_NET_MS : 30000, max: 300000 });
  pokeRef.current = poke;

  useEffect(() => {
    if (!allowed) return undefined;
    window.addEventListener(READ_EVENT, poke);
    return () => window.removeEventListener(READ_EVENT, poke);
  }, [allowed, poke]);

  return allowed ? total : 0;
}

export const ChatIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
